import React, {useEffect, useState} from "react";
import {Button, ButtonGroup, Col, Form, Row} from "react-bootstrap";
import {RequireAuth} from "Components/Security/RequireAuth";
import {DefaultContainer} from "Views/Shared/DefaultContainer";
import {useNavigate, useParams} from "react-router-dom";
import {Notificator} from "Services/Notificator/Notificator";
import {useMutation, useQuery} from "react-query";
import {useAppDispatch} from "hooks";
import {appLoading} from "Store/loadingSlice";
import {ROUTE_PATHS} from "Config/Router/Routes";
import {getMailType} from "Api/Query/MailTypeQuery";
import {updateMailType} from "Api/Mutation/MailTypeMutation";
import {MailType} from "Model/MailType/MailType";
import {RichTextEditor} from "Components/RichTextEditor/RichTextEditor";
import {AdminMailTypePreviewModal} from "./AdminMailTypePreviewModal";

type EditorMode = 'raw' | 'rich'

export const AdminMailTypeEdit: React.FC = () => {

    const {id} = useParams()
    const navigate = useNavigate()
    const dispatch = useAppDispatch()
    const [showPreview, setShowPreview] = useState<boolean>(false)
    const [editorMode, setEditorMode] = useState<EditorMode>('raw')
    const [showTemplate, setShowTemplate] = useState<boolean>(false)

    const [entity, setEntity] = useState<MailType>({
        id: '',
        type: '',
        template: '',
        subject: '',
        body: ''
    })

    const update = useMutation({
        mutationKey: [],
        mutationFn: () => updateMailType(entity.id, {subject: entity.subject, body: entity.body}),
        onSuccess: () => {
            Notificator.success('Se ha editado correctamente')
            navigate(ROUTE_PATHS.ADMIN_MAIL_TYPES_LIST)
        },
        onError: () => {
            Notificator.error('Se ha producido un error al editar')
        }
    })

    const query = useQuery({
        enabled: false,
        queryKey: ['getMailType', id],
        queryFn: () => getMailType(id ?? '-1'),
        onSuccess: (response: any) => {
            setEntity(response.data._result)
        },
        onError: () => {
            Notificator.error('Se ha producido un error al cargar los datos.')
        }
    })

    useEffect(() => {
        query.refetch()
    }, [])

    useEffect(() => {
        dispatch(appLoading({value: query.isLoading, text: 'Cargando'}))
    }, [query.isLoading])

    useEffect(() => {
        dispatch(appLoading({value: update.isLoading, text: 'Guardando'}))
    }, [update.isLoading])

    useEffect(() => {
        dispatch(appLoading({value: query.isRefetching, text: 'Cargando'}))
    }, [query.isRefetching])

    return (
        <RequireAuth>
            <DefaultContainer>
                <Col>
                    <Row>
                        <Col>
                            <h1 className={"pb-4"}>Detalle de plantilla de email</h1>
                        </Col>
                    </Row>

                    <Row className={"mb-3"}>
                        <Col xs={12}>
                            <p><strong>Tipo:</strong> {entity.type}</p>

                            <p className={"mb-1"}>
                                <strong>Template original (referencia): </strong>
                                <Button
                                    variant={"link"}
                                    className={"p-0 ms-2"}
                                    onClick={() => setShowTemplate(!showTemplate)}
                                >
                                    {showTemplate ? 'Ocultar' : 'Ver'}
                                </Button>
                            </p>
                            {showTemplate && (
                                <pre
                                    className={"bg-light border p-3 mb-3"}
                                    style={{fontSize: '0.8rem', maxHeight: '300px', overflowY: 'auto'}}
                                >
                                    {entity.template}
                                </pre>
                            )}
                        </Col>
                    </Row>

                    <Row className={"mb-3"}>
                        <Col xs={12}>
                            <Form.Group className={"mb-3"}>
                                <Form.Label><strong>Asunto</strong></Form.Label>
                                <Form.Control
                                    type={"text"}
                                    value={entity.subject}
                                    onChange={(e) => setEntity({...entity, subject: e.target.value})}
                                    placeholder={"Asunto del email (admite variables Twig {{ variable }})"}
                                />
                            </Form.Group>

                            <Form.Label><strong>Cuerpo</strong></Form.Label>
                            <div className={"mb-2"}>
                                <ButtonGroup size={"sm"}>
                                    <Button
                                        variant={editorMode === 'raw' ? 'secondary' : 'outline-secondary'}
                                        onClick={() => setEditorMode('raw')}
                                    >
                                        Texto plano (Twig)
                                    </Button>
                                    <Button
                                        variant={editorMode === 'rich' ? 'secondary' : 'outline-secondary'}
                                        onClick={() => setEditorMode('rich')}
                                    >
                                        HTML enriquecido
                                    </Button>
                                </ButtonGroup>
                            </div>

                            {editorMode === 'raw' && (
                                <textarea
                                    key={"raw"}
                                    className={"form-control w-100"}
                                    rows={20}
                                    style={{fontFamily: 'monospace', fontSize: '0.85rem'}}
                                    value={entity.body}
                                    onChange={(e) => setEntity({...entity, body: e.target.value})}
                                />
                            )}

                            {editorMode === 'rich' && (
                                <>
                                    <RichTextEditor
                                        key={"rich"}
                                        onChange={(htmlContent) => setEntity({...entity, body: htmlContent})}
                                        defaultContent={entity.body}
                                        height={400}
                                    />
                                    <p className={"text-warning mt-2 mb-0"} style={{fontSize: '0.85rem'}}>
                                        <strong>Aviso:</strong> El editor enriquecido puede modificar la sintaxis Twig. Vuelve a "Texto plano" si tu plantilla usa <code>{'{% %}'}</code> o filtros complejos.
                                    </p>
                                </>
                            )}
                        </Col>
                    </Row>

                    <Row>
                        <Col xs={12} className={"d-flex gap-2"}>
                            <Button
                                variant={"outline-primary"}
                                className={"btn-sm"}
                                onClick={() => setShowPreview(true)}
                            >
                                Vista previa
                            </Button>
                            <Button
                                className={"purple-btn btn-sm"}
                                onClick={() => update.mutate()}
                            >
                                Guardar
                            </Button>
                            <Button
                                variant={"link"}
                                className={"btn-sm"}
                                href={ROUTE_PATHS.ADMIN_MAIL_TYPES_LIST}
                            >
                                Volver al listado
                            </Button>
                        </Col>
                    </Row>
                </Col>

                <AdminMailTypePreviewModal
                    show={showPreview}
                    mailTypeId={entity.id}
                    onClose={() => setShowPreview(false)}
                />
            </DefaultContainer>
        </RequireAuth>
    )
}
